import { type LucideIcon, TrendingDown, TrendingUp } from 'lucide-react';
import React from 'react';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { DeltaDisplay } from '@/pages/analytics/utils/formatters';
import { cn } from '@/utils';

import { InfoTooltip } from './InfoTooltip';

export interface KpiCardProps {
  icon?: LucideIcon;
  iconClassName?: string;
  label: string;
  value: string;
  /** Secondary suffix rendered smaller/muted next to `value` — the "X / N" fraction
   *  pattern in the agents tab (e.g. "20" + "/109"), or a share beside a count. */
  secondaryValue?: string;
  delta?: DeltaDisplay;
  /** Help copy shown on hover. Accepts any React node (string, list, etc.). */
  info?: React.ReactNode;
  /** Optional control at the right edge of the header row (vertical layout only). */
  action?: React.ReactNode;
  /** Visual variant. `horizontal` places the icon left of a stacked value+label
   *  block — matches the Agents tab. `vertical` is the original layout used by
   *  the Org KPI grid where `delta` is rendered. */
  layout?: 'vertical' | 'horizontal';
  loading?: boolean;
}

const toneClass: Record<DeltaDisplay['tone'], string> = {
  positive: 'text-positive-foreground',
  negative: 'text-critical-foreground',
  muted: 'text-muted-foreground',
};

export const KpiCard: React.FC<KpiCardProps> = ({
  icon: Icon,
  iconClassName,
  label,
  value,
  secondaryValue,
  delta,
  info,
  action,
  layout = 'vertical',
  loading,
}) => {
  if (layout === 'horizontal') {
    return (
      <Card className="p-4 flex items-center gap-3 shadow-sm" data-export-pdf-block>
        {Icon && (
          <span
            className={cn(
              // Soft-tinted square badge — 36×36 to match the prototype.
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
              iconClassName,
            )}
          >
            <Icon className="h-5 w-5" />
          </span>
        )}
        {loading ? (
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-3 w-24" />
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="text-2xl font-bold leading-8 text-foreground tabular-nums">
              {value}
              {secondaryValue && (
                <span className="text-sm font-medium text-muted-foreground ml-0.5">
                  {secondaryValue}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground leading-tight">{label}</div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card
      className="p-4 flex flex-col gap-3 shadow-sm h-full justify-between"
      data-export-pdf-block
    >
      <header className="flex items-center gap-2">
        {Icon && (
          <span
            className={cn(
              // Soft tinted badge — sized to match the screenshot's 28px square.
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
              iconClassName,
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
        )}
        <span className="text-[11px] font-medium text-muted-foreground leading-tight truncate">
          {label}
        </span>
        <InfoTooltip content={info} />
        {action && <span className="ml-auto shrink-0">{action}</span>}
      </header>

      {loading ? (
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="text-xl font-semibold leading-7 text-foreground tabular-nums">
            {value}
            {secondaryValue && (
              <span className="ml-1.5 text-sm font-medium text-muted-foreground">
                {secondaryValue}
              </span>
            )}
          </div>
          {delta && (
            <div
              className={cn(
                'inline-flex items-center gap-1 text-xs font-medium',
                toneClass[delta.tone],
              )}
            >
              {delta.arrow === 'up' && <TrendingUp className="h-3.5 w-3.5" />}
              {delta.arrow === 'down' && <TrendingDown className="h-3.5 w-3.5" />}
              <span>{delta.label}</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
